// Function index:
//   openUserDetail(id, ctx, onChange) — fetch + render the per-user detail
//       slide-over over the users list. Hosts every account action (role,
//       verify, profile, disable/enable, reset-pw, delete). onChange re-loads
//       the list behind it after any mutation.

import {
  getAdminUserDetail, setUserRole, verifyUser, updateUserProfile,
  setUserDisabled, resetUserPassword, deleteUser, type AdminUserDetail,
} from '../../../api.js';
import { esc, type AdminPanelCtx } from '../helpers.js';

export async function openUserDetail(id: number, ctx: AdminPanelCtx, onChange: () => void): Promise<void> {
  if (document.querySelector('.user-detail-slideover')) return; // one slide-over at a time
  const aside = document.createElement('aside');
  aside.className = 'user-detail-slideover';
  aside.style.cssText = 'position:fixed; top:0; right:0; height:100vh; width:420px; max-width:90vw; background:#161616; border-left:1px solid #2c2c2c; box-shadow:-8px 0 24px rgba(0,0,0,.4); padding:18px; overflow:auto; z-index:50;';
  const backdrop = document.createElement('div');
  backdrop.style.cssText = 'position:fixed; inset:0; background:rgba(0,0,0,.35); z-index:49;';
  aside.textContent = 'Loading…';
  document.body.appendChild(backdrop);
  document.body.appendChild(aside);

  const close = (): void => {
    aside.remove(); backdrop.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  let d: AdminUserDetail;
  try {
    d = await getAdminUserDetail(id);
  } catch (err) {
    aside.textContent = `Could not load user: ${(err as Error).message}`;
    return;
  }
  render();

  function render(): void {
    const u = d.user;
    const isMe = u.id === ctx.user.id;
    aside.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:start;">
        <h2 style="margin:0;">${esc(u.email)}</h2>
        <button class="secondary d-close">✕</button>
      </div>
      <div class="party-meta">id ${u.id} · ${u.role} · ${u.email_verified ? 'verified' : 'unverified'} · ${u.disabled ? 'disabled' : 'active'}</div>

      <div style="margin:14px 0; display:flex; gap:6px; flex-wrap:wrap;">
        ${u.role === 'admin'
          ? `<button class="secondary d-demote" ${isMe ? 'disabled title="cannot demote yourself"' : ''}>Demote</button>`
          : `<button class="secondary d-promote">Promote</button>`}
        ${u.email_verified ? '' : '<button class="secondary d-verify">Force verify</button>'}
        ${u.disabled
          ? '<button class="secondary d-enable">Enable</button>'
          : (isMe ? '' : '<button class="secondary d-disable">Disable</button><button class="secondary d-reset">Reset pw</button><button class="secondary d-delete">Delete</button>')}
      </div>

      <div style="margin:10px 0;">
        name <input class="d-name" value="${esc(u.display_name)}" style="width:140px;">
        email <input class="d-email" value="${esc(u.email)}" style="width:200px;">
        <button class="secondary d-save">Save</button>
      </div>

      <h3>Spend</h3>
      <div class="party-meta">total $${d.spend.total_usd.toFixed(2)} · last 30d $${d.spend.last_30d_usd.toFixed(2)}</div>
      ${d.spend.by_model.map((m) => `<div class="party-meta">${esc(m.model)}: $${m.usd.toFixed(2)}</div>`).join('')}

      <h3>Parties (${d.parties.length})</h3>
      ${d.parties.map((p) => `<div class="party-meta">${esc(p.name)} · ${esc(p.world_name)}</div>`).join('') || '<div class="party-meta">none</div>'}

      <h3>Characters (${d.characters.length})</h3>
      ${d.characters.map((c) => `<div class="party-meta">${esc(c.name)} · ${esc(c.world_name)}</div>`).join('') || '<div class="party-meta">none</div>'}

      <h3>Recent actions</h3>
      ${d.recent_audit.map((a) => `<div class="party-meta">${esc(new Date(a.at).toLocaleString())} · ${esc(a.action)} · ${esc(a.actor_email ?? '—')}</div>`).join('') || '<div class="party-meta">none</div>'}
    `;

    const act = async (fn: () => Promise<unknown>, info?: string): Promise<void> => {
      try {
        await fn();
        if (info) ctx.setInfo(info);
        d = await getAdminUserDetail(id); // refresh panel
        render();
        onChange();                        // refresh list behind
      } catch (err) { ctx.setError((err as Error).message); }
    };

    aside.querySelector('.d-close')?.addEventListener('click', close);
    aside.querySelector('.d-promote')?.addEventListener('click', () => void act(() => setUserRole(id, 'admin')));
    aside.querySelector('.d-demote')?.addEventListener('click', () => void act(() => setUserRole(id, 'user')));
    aside.querySelector('.d-verify')?.addEventListener('click', () => void act(() => verifyUser(id)));
    aside.querySelector('.d-enable')?.addEventListener('click', () => void act(() => setUserDisabled(id, false)));
    aside.querySelector('.d-disable')?.addEventListener('click', () => void act(() => setUserDisabled(id, true)));
    aside.querySelector('.d-reset')?.addEventListener('click', () => void act(() => resetUserPassword(id), 'reset email sent'));
    aside.querySelector('.d-delete')?.addEventListener('click', () => {
      if (!confirm('Anonymize and disable this user? Their content stays but they can\'t log in.')) return;
      void (async () => {
        try {
          await deleteUser(id);
          ctx.setInfo('user anonymized + disabled');
          close();
          onChange();
        } catch (err) { ctx.setError((err as Error).message); }
      })();
    });
    aside.querySelector('.d-save')?.addEventListener('click', () => {
      const name = (aside.querySelector('.d-name') as HTMLInputElement).value.trim();
      const email = (aside.querySelector('.d-email') as HTMLInputElement).value.trim();
      void act(() => updateUserProfile(id, { display_name: name, email }), 'profile updated');
    });
  }
}
